export interface PersonNameInfo {
  prefix?: string | null;
  first?: string | null;
  middleInitial?: string | null;
  last?: string | null;
  suffix?: string | null;
  preferred?: string | null;
  organization?: string | null;
}

export interface GetNameOptions {
  includeSuffix?: boolean;
  includePrefix?: boolean;
  includeMiddleInitial?: boolean;
  ignorePreferredName?: boolean;
  maxLength?: number;
  lastInitial?: boolean;
}

export class PersonNameInfoClass implements PersonNameInfo {
  prefix?: string | null;
  first?: string | null;
  middleInitial?: string | null;
  last?: string | null;
  suffix?: string | null;
  preferred?: string | null;
  organization?: string | null;

  constructor(data: Partial<PersonNameInfo> = {}) {
    this.prefix = data.prefix ?? null;
    this.first = data.first ?? null;
    this.middleInitial = data.middleInitial ?? null;
    this.last = data.last ?? null;
    this.suffix = data.suffix ?? null;
    this.preferred = data.preferred ?? null;
    this.organization = data.organization ?? null;
  }

  /**
   * Equivalent to PersonUtil.getPersonName(nameInfo, options)
   */
  getFullName(options: GetNameOptions = {}): string {
    const nameBaseline = this.getNameInternal(options, false, false);
    if (!options.maxLength) return nameBaseline;
    const maxLength = Math.max(options.maxLength - 1, 0);
    if (nameBaseline.length > maxLength) {
      const shortOptions = {
        ...options,
        includePrefix: false,
        includeSuffix: false,
        includeMiddleInitial: false,
      };
      const nameShort = this.getNameInternal(shortOptions, false, false);
      if (nameShort.length > maxLength) {
        const nameShorter = this.getNameInternal(shortOptions, false, true);
        console.log(nameShorter);
        if (nameShorter.length > maxLength) {
          const nameShortest = this.getNameInternal(shortOptions, true, true);
          console.log(nameShortest);
          return nameShortest;
        }
        return nameShorter;
      }
      return nameShort;
    }
    return nameBaseline;
  }

  capitalizeFirst(name: string): string {
    if (name.length == 0) return name;
    return name.substring(0, 1).toUpperCase() + name.substring(1);
  }

  getNameInternal(
    options: GetNameOptions = {},
    truncateFirst: boolean,
    truncateLast: boolean,
  ): string {
    const {
      includeSuffix = false,
      includePrefix = false,
      includeMiddleInitial = false,
      ignorePreferredName = false,
    } = options;

    if (this.organization != null) return this.organization;

    const prefix = includePrefix && this.prefix ? `${this.prefix} ` : "";
    const middle =
      includeMiddleInitial && this.middleInitial
        ? `${this.middleInitial} `
        : "";
    const suffix = includeSuffix && this.suffix ? ` ${this.suffix}` : "";

    const first = this.capitalizeFirst(
      !ignorePreferredName && this.preferred
        ? this.preferred
        : (this.first ?? ""),
    );
    const lastInitial = this.last?.at(0)?.toUpperCase()?.concat(".") || "";
    const last = options.lastInitial
      ? lastInitial
      : this.capitalizeFirst(this.last ?? "");

    const maxLength = options.maxLength
      ? Math.max(options.maxLength - 1, 0)
      : undefined;

    const firstTrunacted = truncateFirst
      ? first.substring(
          0,
          maxLength ? Math.max(maxLength - 4, 0) : first.length,
        ) + "..."
      : first;

    if (first == "" && last == "") return "Member"; //Handle no name

    const lastTruncated = truncateLast ? last.substring(0, 1) + "." : last;

    return `${prefix}${firstTrunacted} ${middle}${lastTruncated}${suffix}`.trim();
  }

  static fromPersonOnlyDTO(person: {
    namePrefix?: string | null;
    nameFirst?: string | null;
    nameMiddleInitial?: string | null;
    nameLast?: string | null;
    nameSuffix?: string | null;
  }): PersonNameInfoClass {
    return new PersonNameInfoClass({
      prefix: person.namePrefix,
      first: person.nameFirst,
      middleInitial: person.nameMiddleInitial,
      last: person.nameLast,
      suffix: person.nameSuffix,
      preferred: null,
    });
  }
}
